import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';

import { useMusicLibraryData } from './hooks';
import { MUSIC_LIBRARY_VARIABLES } from './constants';
import Tracks from './Tracks';
import Categories from './Categories';

import { RichText } from 'components/Prismic';
import { ArrayElement, Normalize } from 'utils/types';
import { filterProps } from 'utils/helpers';

export type TMusicLibrary = ArrayElement<
  Normalize<Queries.MusicLibraryDataFragment['data']['content']>
>;

const Container = styled('div', filterProps('isDark'))<{ isDark?: boolean }>`
  ${({ theme: { colors, mq, spacings, tp }, isDark }) => css`
    ${MUSIC_LIBRARY_VARIABLES.titleColor}: ${isDark ? '#d0d0d0' : colors.primary};
    ${MUSIC_LIBRARY_VARIABLES.titleWeight}: ${isDark
      ? tp.fontWeightsByFamily['Switzer'].lightest
      : tp.fontWeightsByFamily['Switzer'].regular};
    ${MUSIC_LIBRARY_VARIABLES.categoryTextColor}: ${isDark ? '#a5a5a5' : colors.black};
    ${MUSIC_LIBRARY_VARIABLES.categoryHighlightTextColor}: ${isDark ? colors.black : colors.white};
    ${MUSIC_LIBRARY_VARIABLES.iconColor}: ${isDark ? '#d0d0d0' : colors.black};
    ${MUSIC_LIBRARY_VARIABLES.trackContainerColor}: ${isDark ? '#ffffff06' : colors.white};
    ${MUSIC_LIBRARY_VARIABLES.indicatorBackgroundColor}: ${isDark ? '#252525' : colors.white};
    ${MUSIC_LIBRARY_VARIABLES.progressBarColor}: ${isDark ? '#e5ebf833' : '#a4a4a433'};
    ${MUSIC_LIBRARY_VARIABLES.trackNameColor}: ${isDark ? '#edeef0' : colors.black};
    ${MUSIC_LIBRARY_VARIABLES.trackGenresColor}: ${isDark ? '#edeef0' : '#323232'};
    ${MUSIC_LIBRARY_VARIABLES.accentColor}: ${isDark ? colors.primary : colors.black};
    ${MUSIC_LIBRARY_VARIABLES.selectBackgroundColor}: ${isDark ? '#292929' : colors.white};
    ${MUSIC_LIBRARY_VARIABLES.selectTextColor}: ${isDark
      ? `${colors.white}99`
      : `${colors.black}99`};
    ${MUSIC_LIBRARY_VARIABLES.selectActiveTextColor}: ${isDark ? colors.white : colors.black};

    display: grid;
    position: relative;
    row-gap: 7.5rem;
    justify-content: center;
    margin-bottom: -1px;
    background-color: ${isDark ? colors.backgroundDark : colors.offWhite};
    padding: 25rem 0;
    overflow: hidden;

    ${mq.md} {
      && {
        display: grid;
        row-gap: 5rem;
        padding: 6rem ${spacings.padding};
      }
    }
  `}
`;

const Title = styled(RichText)`
  ${({ theme: { mq, tp } }) => css`
    display: flex;
    flex-direction: column;

    & > h2 {
      text-align: center;
      line-height: 1.2;
      letter-spacing: -0.065em;
      color: var(${MUSIC_LIBRARY_VARIABLES.titleColor});
      font-size: 10rem;
      font-weight: var(${MUSIC_LIBRARY_VARIABLES.titleWeight});

      ${tp.useFont(fonts => fonts.Switzer.thin)};

      strong {
        line-height: 1.1;
        ${tp.useFont(fonts => fonts.Switzer.regular)};
      }
    }

    ${mq.md} {
      & > h2 {
        line-height: 1;
        font-size: 5.25rem;

        ${tp.useFont(fonts => fonts.Switzer.light)};
      }
    }
  `}
`;

const Player = styled.div`
  ${({ theme: { mq } }) => css`
    display: flex;
    column-gap: 6rem;

    ${mq.md} {
      display: grid;
      row-gap: 1.5rem;
    }
  `}
`;

const MusicLibrary: React.FC<TMusicLibrary> = sliceData => {
  const props = useMusicLibraryData(sliceData);
  if (!props) return null;

  const { isDark, title, categoriesProps, tracksProps } = props;

  return (
    <Container isDark={isDark}>
      <Title render={title} />
      <Player>
        <Categories {...categoriesProps} />
        <Tracks {...tracksProps} />
      </Player>
    </Container>
  );
};

export const contentMusicLibraryFragment = graphql`
  fragment MusicLibraryData on MusicLibraryEnabledPage {
    ...MusicLibraryCategoriesData
    ...MusicLibraryTracksData

    ... on PrismicSolution {
      data {
        content {
          ... on PrismicSolutionDataContentMusicLibrary {
            slice_type

            primary {
              music_library_title {
                richText
              }
              music_library_is_dark
            }
          }
        }
      }
    }

    ... on PrismicIndustry {
      data {
        content {
          ... on PrismicIndustryDataContentMusicLibrary {
            slice_type

            primary {
              music_library_title {
                richText
              }
              music_library_is_dark
            }
          }
        }
      }
    }
  }
`;

export default MusicLibrary;
